/**
 * AdLandingPage — dedicated full-screen ad/campaign landing page for Facebook /
 * Google ad traffic. Single conversion goal: capture an Early Access email.
 *
 * Routes: /early-access-ad, /ja/early-access-ad, /en/early-access-ad
 *
 * - Minimal chrome (logo + language toggle only) — no site nav, no dashboard links.
 * - Japanese default with an English toggle; key copy rendered bilingually.
 * - Desktop: hero content left, signup form right. Mobile: headline → form → points.
 * - Reuses the EXISTING Early Access backend (POST /api/early-access/join) — same
 *   100,000-spot list. Preserves Facebook/Google UTM params (full source_url +
 *   utm_source/medium/campaign) and tags the funnel as `early_access_ad`.
 * - Reuses the real-time flow animation (SimulatedAIPipelineDemo) to show how the
 *   daily receipts are produced & delivered.
 *
 * Guardrails: no billing / Stripe / live MEISAI / sensitive-data collection.
 */
import { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { Jp } from '@/lib/jaText';
import { useEarlyAccessCounts } from '@/lib/useEarlyAccessCounts';
import { metaTrack } from '@/lib/metaPixel';
import { recordAttribution } from '@/lib/attribution';
import SimulatedAIPipelineDemo from '@/components/marketing/SimulatedAIPipelineDemo';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  CreditCard, Car, FileText, BarChart3, ShieldCheck, Users,
  Cloud, Clock, Headphones, ArrowRight, ExternalLink, Lock, Check,
  Sparkles, Globe, Mail, UserRound, Briefcase, Landmark, Truck,
  KeyRound, Receipt, HelpCircle, Workflow, AlertCircle, Share2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Brand + team imagery (official JTR assets).
const LOGO_WORDMARK = '/assets/logo/japantollreceipts.gif';
const TEAM_PHOTOS = [
  'https://customer-assets.emergentagent.com/job_1c159d1f-49ee-4e06-a225-61198d3194d3/artifacts/8n9kz8hc_17c560c6-02b4-45ca-aeae-2fc46466a286.png',
  'https://customer-assets.emergentagent.com/job_1c159d1f-49ee-4e06-a225-61198d3194d3/artifacts/xzncdc1t_36d86944-df12-46c1-8306-c1f1f72049ff.png',
  'https://customer-assets.emergentagent.com/job_1c159d1f-49ee-4e06-a225-61198d3194d3/artifacts/pvdfwtve_600c7f4e-7150-4e37-9063-0f129c645390.png',
  'https://customer-assets.emergentagent.com/job_1c159d1f-49ee-4e06-a225-61198d3194d3/artifacts/p330qf0h_2632ac0a-4924-49f0-865f-436e109e5dd0.png',
  'https://customer-assets.emergentagent.com/job_1c159d1f-49ee-4e06-a225-61198d3194d3/artifacts/ki2i8obk_48450c38-3874-47b8-9099-245c0b878ce2.png',
];
// Language-aware branded header banner (approved 2026-06-02, hosted locally).
// WebP primary (~89% smaller, visually lossless at q=85) with PNG fallback for
// browsers that don't support WebP — Professional Polish Audit 2026-02 asset
// compression pass. Originals kept alongside for instant rollback.
const HERO_BANNER_JA_WEBP = '/assets/headers/jtr_header_ja.webp';
const HERO_BANNER_JA = '/assets/headers/jtr_header_ja.png';
const HERO_BANNER_EN_WEBP = '/assets/headers/jtr_header_en.webp';
const HERO_BANNER_EN = '/assets/headers/jtr_header_en.png';

const VALUE_POINTS = [
  { Icon: CreditCard, ja: 'ETCカード・車両の一元管理', en: 'Centralized ETC card & vehicle records' },
  { Icon: FileText, ja: '利用明細の自動取得・レポート化', en: 'Automatic toll data retrieval & reporting' },
  { Icon: BarChart3, ja: 'PDF・CSV 形式の通行レポート', en: 'Daily / weekly toll reports (PDF + CSV)' },
  { Icon: Users, ja: '組織・部署ごとの権限管理', en: 'Business & Government multi-vehicle oversight' },
  { Icon: Sparkles, ja: '1年間プレミアム無料体験（先行受付）', en: '1-Year Premium Trial for Early Access' },
];

const FEATURES = [
  { Icon: CreditCard, ja: 'ETCカード管理', en: 'ETC Card Management', dja: 'カードの登録・更新・ステータス管理', den: 'Register, renew & track card status' },
  { Icon: Car, ja: '複数車両の利用記録', en: 'Multi-vehicle records', dja: '車両情報・OBU・割当の記録', den: 'Vehicle, OBU & assignment records' },
  { Icon: FileText, ja: '利用明細の自動取得', en: 'Automatic Toll Retrieval', dja: '明細取得を自動化し手作業を削減', den: 'Automate retrieval, cut manual work' },
  { Icon: BarChart3, ja: 'レポート・分析', en: 'Reports & Analytics', dja: '組織別・車両別にコストを可視化', den: 'Cost visibility by org & vehicle' },
  { Icon: ShieldCheck, ja: '確認が必要な利用のレビュー・アラート', en: 'Unusual Usage Review', dja: '不審・異常な利用を検知して通知', den: 'Detect & flag unusual usage' },
  { Icon: Users, ja: '組織・権限管理', en: 'Organization & Permissions', dja: '部署・役割ごとのアクセス制御', den: 'Role-based access for organizations' },
];

const TRUST = [
  { Icon: ShieldCheck, ja: 'セキュリティ最優先', en: 'Security First', dja: '情報は安全に暗号化され保護されます', den: 'Your data is encrypted & protected' },
  { Icon: Cloud, ja: 'クラウドでいつでも', en: 'Cloud Access', dja: 'インターネット環境があればどこからでも', den: 'Access anywhere with a browser' },
  { Icon: Clock, ja: '業務を大幅に削減', en: 'Save Time', dja: '明細取得・管理を自動化', den: 'Automate retrieval & management' },
  { Icon: Headphones, ja: '安心のサポート体制', en: 'Reliable Support', dja: '日本語・英語でのサポートを提供', den: 'Support in Japanese & English' },
];

const ACCOUNT_TYPES = [
  { key: 'personal', ja: '個人', en: 'Personal' },
  { key: 'business', ja: '法人', en: 'Business' },
  { key: 'government', ja: '官公庁・公共部門', en: 'Government' },
];

// "Who is Japan Toll Receipts for?" — universal audience chips.
const WHO_FOR = [
  { Icon: UserRound, ja: '個人ドライバー', en: 'Individuals' },
  { Icon: Briefcase, ja: '法人・企業', en: 'Businesses' },
  { Icon: Landmark, ja: '官公庁・公共部門', en: 'Government organizations' },
  { Icon: Car, ja: '複数車両運用', en: 'Fleet operators' },
  { Icon: KeyRound, ja: 'レンタカー事業者', en: 'Rental car companies' },
  { Icon: Truck, ja: '物流・運送', en: 'Logistics companies' },
];

// "How it works" — universal 4-step flow.
const HOW_STEPS = [
  { Icon: CreditCard, ja: 'ETCカード・車両を登録', en: 'Register ETC cards & vehicles', dja: 'カードと車両情報を登録', den: 'Add your cards and vehicles' },
  { Icon: Cloud, ja: '利用明細を自動取得', en: 'Auto-retrieve toll usage', dja: 'ETC利用照会サービスから取得', den: 'Pulled from the ETC inquiry service' },
  { Icon: ShieldCheck, ja: 'データを検証・整理', en: 'Validate & organize', dja: '内容を検証し整形', den: 'Checked and structured for you' },
  { Icon: Receipt, ja: 'PDF・CSVでお届け', en: 'Delivered as PDF + CSV', dja: 'メールに自動配信', den: 'Emailed on your schedule' },
];

// Per-audience hero copy + "problems we solve" + FAQ. All bilingual.
const AUDIENCE = {
  general: {
    h1ja: { pre: '日本のETC管理を、', accent: 'もっとスマート', post: 'に。' },
    h1en: { pre: 'Smarter ETC management for a ', accent: 'faster', post: ' Japan.' },
    subja: 'ETCカード・車両・通行料金データを一元管理。利用明細を自動取得し、毎日のレシートをPDF・CSVでお届けします。',
    suben: 'Centralize your ETC cards, vehicles, and toll data. We retrieve your usage automatically and deliver your daily receipts as PDF + CSV.',
    problems: [
      { ja: '手作業のETC明細管理', en: 'Manual ETC tracking' },
      { ja: 'レシートの紛失', en: 'Missing receipts' },
      { ja: '通行料の反映が遅い', en: 'Delayed toll visibility' },
      { ja: '複数車両の可視化不足', en: 'Limited multi-vehicle oversight' },
      { ja: '車両への割当漏れ', en: 'Vehicle assignment gaps' },
      { ja: 'レポート作成の手間', en: 'Reporting challenges' },
    ],
  },
  personal: {
    h1ja: { pre: '日本のETC利用明細を、', accent: '自動で受け取る', post: '。' },
    h1en: { pre: 'Get your Japan ETC toll receipts ', accent: 'automatically', post: '.' },
    subja: '通勤・出張・確定申告に。ETC利用明細を毎日・毎週メールで自動受信し、PDF・CSVで保管できます。',
    suben: 'For commuting, business trips, and tax filing. Receive your ETC toll receipts automatically by email — as PDF + CSV you can keep.',
    problems: [
      { ja: '手作業のETC明細管理', en: 'Manual ETC tracking' },
      { ja: 'レシートの紛失', en: 'Missing receipts' },
      { ja: '通行料の反映が遅い', en: 'Delayed toll visibility' },
      { ja: '確定申告の手間', en: 'Tax-filing hassle' },
      { ja: '家計と経費の区分', en: 'Separating personal vs work' },
      { ja: '過去の利用履歴が消える', en: 'Losing toll history' },
    ],
  },
  business: {
    h1ja: { pre: 'ETCカード・車両・拠点を、', accent: 'ひとつで管理', post: '。' },
    h1en: { pre: 'ETC cards, vehicles & branches — ', accent: 'all in one place', post: '.' },
    subja: '複数のETCカード・社用車・拠点をまとめて管理。コストを可視化し、PDF・CSVで経費精算とエクスポートを効率化します。',
    suben: 'Manage ETC cards, vehicles, branches, and toll reports in one place. See costs clearly and export PDF/CSV for reimbursement.',
    problems: [
      { ja: '車両・カードの台帳管理', en: 'Vehicle & card oversight' },
      { ja: '拠点・部署別の集計', en: 'Branch & department rollups' },
      { ja: '経費精算の手作業', en: 'Manual reimbursement' },
      { ja: 'コストの可視化不足', en: 'Limited cost visibility' },
      { ja: '車両への割当漏れ', en: 'Vehicle assignment gaps' },
      { ja: '社内確認向けの記録整理', en: 'Organized for customer review recordkeeping' },
    ],
  },
  government: {
    h1ja: { pre: '公用車ETCの可視化と', accent: '説明責任', post: 'を、ひとつの画面で。' },
    h1en: { pre: 'Oversight, delegated access & ', accent: 'audit visibility', post: ' for public-sector ETC fleets.' },
    subja: '部署・指揮系統に沿った権限委譲、レビュー可視化、確認対応のレポート。公用車ETCの説明責任を支えます。',
    suben: 'Delegated permissions by department, review visibility, and reporting organized for review for public-sector ETC fleets.',
    problems: [
      { ja: '部署・指揮系統の管理', en: 'Department & command structure' },
      { ja: '監査・要確認項目の確認', en: 'Audit & review visibility' },
      { ja: '権限委譲の管理', en: 'Delegated access control' },
      { ja: '説明責任の確保', en: 'Accountability' },
      { ja: '目的外利用の兆候', en: 'Possible out-of-policy use' },
      { ja: '記録の保管・照合', en: 'Record retention & reconciliation' },
    ],
  },
};

// FAQ — first three are shared (independence / cost / signup); the 4th is
// audience-specific. Reuses the same accordion UX as the marketing pages.
const FAQ_COMMON = [
  {
    qja: 'Japan Toll Receipts は NEXCO や ETC利用照会サービスですか？', qen: 'Is this NEXCO or the ETC Usage Inquiry Service?',
    aja: 'いいえ。当社は独立した第三者のレポート・自動化サービスで、NEXCO・ETC利用照会サービス・有料道路事業者ではありません。', aen: 'No. We are an independent third-party reporting & automation service — not NEXCO, the ETC Usage Inquiry Service, or a toll-road operator.',
  },
  {
    qja: '先行受付の登録に費用はかかりますか？', qen: 'Does Early Access cost anything?',
    aja: '無料です。先行受付枠で登録された方には、ローンチ時に1年間のプレミアム無料トライアルをご案内します。クレジットカードは不要です。', aen: 'It is free. Early Access members get a 1-year Premium free trial at launch. No credit card required.',
  },
  {
    qja: '今すぐアカウントを作る必要がありますか？', qen: 'Do I need to create an account now?',
    aja: 'いいえ。メールアドレスのご登録だけで結構です。ローンチ準備が整い次第、優先的にご案内します。', aen: 'No — just your email. We will notify you with priority access as soon as launch is ready.',
  },
];
const AUDIENCE_FAQ = {
  general: { qja: '対応している利用者は？', qen: 'Who is it for?', aja: '個人・法人・官公庁・複数車両・レンタカー・物流など、幅広い利用者に対応します。', aen: 'Individuals, businesses, government, fleets, rental-car companies, and logistics operators.' },
  personal: { qja: 'ETCカードは何枚まで登録できますか？', qen: 'How many ETC cards can I track?', aja: '個人プランは最大10枚のETCカードに対応します。', aen: 'Personal plans support up to 10 tracked ETC cards.' },
  business: { qja: '何枚・何台まで管理できますか？', qen: 'How many cards / vehicles can we manage?', aja: '法人プランは最大60,000枚規模のETCカードと多数の車両・拠点を一元管理できます。', aen: 'Business plans scale to ~60,000 ETC cards across many vehicles and branches.' },
  government: { qja: '権限委譲や監査担当の閲覧はできますか？', qen: 'Do you support delegated access and auditors?', aja: 'はい。部署・指揮系統に沿った権限委譲と、監査担当者向けの読み取り専用ビューに対応します。', aen: 'Yes — delegated permissions by department/command structure and read-only views for auditors.' },
};

export default function AdLandingPage({ audience = 'general' }) {
  const { lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const ja = lang === 'ja';

  // Switch language by rewriting the URL's /ja ↔ /en prefix. These pages live
  // under LangPathEnforcer, which syncs language FROM the URL prefix — so a
  // plain state toggle gets instantly reverted. Changing the prefix is the
  // single source of truth and makes the JA/EN switch actually stick.
  const switchLang = (target) => {
    if (target === lang) return;
    let path = location.pathname;
    if (path === '/ja' || path.startsWith('/ja/')) path = path.slice(3) || '/';
    else if (path === '/en' || path.startsWith('/en/')) path = path.slice(3) || '/';
    setLang(target);
    navigate(`/${target}${path}${location.search}${location.hash}`);
  };
  const counts = useEarlyAccessCounts();
  const cfg = AUDIENCE[audience] || AUDIENCE.general;
  const faq = [...FAQ_COMMON, AUDIENCE_FAQ[audience] || AUDIENCE_FAQ.general];

  const [track, setTrack] = useState(() => {
    if (audience !== 'general') return audience; // targeted page → pre-select account type
    const t = new URLSearchParams(window.location.search).get('type');
    return ['personal', 'business', 'government'].includes(t) ? t : 'personal';
  });
  const [form, setForm] = useState({ email: '', name: '', organization_name: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const honeypot = useRef('');
  const formStartedRef = useRef(false);

  // Conversion optimization: on a targeted ad page the audience is already
  // pre-selected (the visitor self-selected by clicking the ad), so collapse
  // the 3-way account-type chooser into a confirmed pill with a "change" link
  // to remove one decision before the email. General pages keep the chooser.
  const isTargeted = audience !== 'general';
  const [showTypeChooser, setShowTypeChooser] = useState(!isTargeted);

  const isOrg = track === 'business' || track === 'government';
  const remaining = counts.remaining != null ? counts.remaining : 100000;
  const fmt = (n) => Number(n || 0).toLocaleString();

  const sourcePageType = audience === 'general' ? 'early_access_ad' : `early_access_ad_${audience}`;

  // Meta Instant Form follow-up mode: when a visitor arrives from a Facebook
  // Instant Form thank-you screen (`?joined=1` or `?source=meta_instant_form`),
  // hide the signup form and show a confirmation + "learn how it works"
  // experience instead. The lead was already captured inside Meta, so we never
  // fire a Lead event here — only PageView/ViewContent. UTMs are preserved.
  const joined = (() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('joined') === '1' || p.get('source') === 'meta_instant_form';
  })();

  // Anonymous funnel tracking (no PII) → Marketing Command Center conversion stats.
  const trackEvent = (step) => {
    try {
      const params = new URLSearchParams(window.location.search);
      const body = JSON.stringify({
        step,
        source_page_type: sourcePageType,
        variant: sourcePageType,
        landing_page: window.location.pathname,
        account_type: track,
        language: lang === 'ja' ? 'ja' : 'en',
        utm_source: params.get('utm_source') || '',
        utm_medium: params.get('utm_medium') || '',
        utm_campaign: params.get('utm_campaign') || '',
        utm_content: params.get('utm_content') || '',
        utm_term: params.get('utm_term') || '',
        referrer: document.referrer || '',
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${API}/api/marketing/track`, new Blob([body], { type: 'application/json' }));
      } else {
        fetch(`${API}/api/marketing/track`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true });
      }
    } catch { /* tracking is best-effort */ }
  };

  // Fire form_started once, the first time the visitor interacts with the form.
  const markFormStarted = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    trackEvent('form_started');
  };

  // Fire one page_view per mount. In Meta Instant Form follow-up mode we also
  // emit ViewContent — but never Lead (the lead was already captured by Meta).
  useEffect(() => {
    trackEvent('page_view');
    if (joined) {
      try { metaTrack.viewContent({ source: 'meta_instant_form', account_type: track, variant: sourcePageType }); } catch { /* noop */ }
    }
    /* eslint-disable-next-line */
  }, []);

  // External links open in a NEW tab (ad page keeps the visitor on the form).
  const ext = (path) => ({ href: `/${lang}${path}`, target: '_blank', rel: 'noopener noreferrer' });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    trackEvent('cta_click');
    if (!form.email || !form.email.includes('@')) {
      setError(ja ? '有効なメールアドレスを入力してください。' : 'Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const res = await fetch(`${API}/api/early-access/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email.trim(),
          account_type: track,
          preferred_language: lang === 'ja' ? 'ja' : 'en',
          name: form.name,
          organization_name: isOrg ? form.organization_name : '',
          estimated_etc_card_count: null,
          consent_email_updates: true,
          privacy_acknowledged: true,
          // Preserve the FULL ad URL (keeps every Facebook/Google UTM param) +
          // the standard UTM fields the backend stores discretely.
          source_url: window.location.href,
          source_page_type: sourcePageType,
          utm_source: params.get('utm_source') || '',
          utm_medium: params.get('utm_medium') || '',
          utm_campaign: params.get('utm_campaign') || '',
          utm_content: params.get('utm_content') || '',
          utm_term: params.get('utm_term') || '',
          company_website: honeypot.current,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || (ja ? '登録に失敗しました。' : 'Something went wrong.'));
        return;
      }
      setSuccess(data);
      trackEvent('email_submitted');
      try { metaTrack.lead({ email: form.email.trim() }, { account_type: track, source: sourcePageType }); } catch { /* noop */ }
      try { recordAttribution('waitlist', { account_type: track }); } catch { /* noop */ }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      try { localStorage.setItem('ea_joined', '1'); } catch { /* noop */ }
    } catch (err) {
      setError(err.message || (ja ? 'ネットワークエラー。' : 'Network error.'));
    } finally {
      setLoading(false);
    }
  };

  // Small bilingual label: primary (by lang) bold + secondary muted.
  const FieldLabel = ({ jt, en }) => (
    <label className="block mb-1.5">
      <span className="text-sm font-bold text-slate-800">{ja ? jt : en}</span>
      <span className="ml-1.5 text-xs font-normal text-slate-400">{ja ? en : jt}</span>
    </label>
  );

  return (
    <div className="min-h-screen bg-white text-slate-900 pb-20 lg:pb-0" data-testid="ad-landing-page">
      {/* ── Minimal top bar (logo + language toggle ONLY) ─────────────── */}
      <header className="absolute top-0 inset-x-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center" data-testid="ad-brand">
            <div className="inline-flex items-center rounded-2xl bg-white px-4 py-2.5 shadow-lg ring-1 ring-black/5">
              <img
                src={LOGO_WORDMARK}
                alt="Japan Toll Receipts"
                className="h-14 sm:h-16 w-auto object-contain"
                data-testid="ad-brand-logo"
              />
            </div>
          </div>
          <div className="inline-flex items-center rounded-full bg-white/10 backdrop-blur ring-1 ring-white/20 p-0.5" data-testid="ad-lang-toggle">
            <button
              type="button"
              onClick={() => switchLang('ja')}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${ja ? 'bg-white text-slate-900' : 'text-white/80 hover:text-white'}`}
              data-testid="ad-lang-ja"
            >日本語</button>
            <button
              type="button"
              onClick={() => switchLang('en')}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${!ja ? 'bg-white text-slate-900' : 'text-white/80 hover:text-white'}`}
              data-testid="ad-lang-en"
            >English</button>
          </div>
        </div>
      </header>

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0a1a33] via-[#1b1148] to-[#2c0a52]" data-testid="ad-hero">
        {/* decorative road / grid motif (CSS only — no heavy image) */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.12]"
          style={{ backgroundImage: 'linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px)', backgroundSize: '46px 46px' }} />
        <div className="pointer-events-none absolute -top-24 -right-24 w-[36rem] h-[36rem] rounded-full bg-[#9200C7]/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-24 w-[34rem] h-[34rem] rounded-full bg-emerald-500/20 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10 sm:pt-24 sm:pb-12 lg:pt-28 lg:pb-20">
          <div className="lg:grid lg:grid-cols-12 lg:gap-10 lg:items-start">

            {/* Headline (DOM 1 → mobile top) */}
            <div className="lg:col-span-7 lg:col-start-1 lg:row-start-1">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/30 px-3 py-1 mb-5" data-testid="ad-counter-chip">
                <Sparkles className="w-3.5 h-3.5 text-emerald-300" strokeWidth={2.6} />
                <span className="text-[11px] font-bold text-emerald-100">
                  {ja
                    ? `先行受付枠 残り ${fmt(remaining)} ／ 100,000`
                    : `${fmt(remaining)} of 100,000 Early Access spots left`}
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.08] text-white">
                {ja ? (
                  <>{cfg.h1ja.pre}<span className="text-emerald-400">{cfg.h1ja.accent}</span>{cfg.h1ja.post}</>
                ) : (
                  <>{cfg.h1en.pre}<span className="text-emerald-400">{cfg.h1en.accent}</span>{cfg.h1en.post}</>
                )}
              </h1>
              <p className="mt-4 text-base sm:text-lg text-white/80 max-w-xl leading-relaxed">
                {ja ? <Jp>{cfg.subja}</Jp> : cfg.suben}
              </p>
              <p className="mt-1.5 text-sm text-white/55 max-w-xl">
                {ja ? cfg.suben : cfg.subja}
              </p>
            </div>

            {/* Signup form (DOM 2 → mobile near top; desktop right, spans both rows) */}
            <div className="mt-8 lg:mt-0 lg:col-span-5 lg:col-start-8 lg:row-start-1 lg:row-span-2">
              {joined ? (
                <JoinedCard ja={ja} lang={lang} />
              ) : success ? (
                <SuccessCard ja={ja} data={success} lang={lang} />
              ) : (
                <form
                  onSubmit={submit}
                  id="ad-signup"
                  className="rounded-3xl bg-white shadow-2xl ring-1 ring-black/5 p-5 sm:p-6"
                  data-testid="ad-signup-form"
                >
                  <div className="text-center mb-4">
                    <h2 className="text-xl font-black text-slate-900">{ja ? '無料で始める' : 'Start Free'}</h2>
                    <p className="text-xs text-slate-500 mt-0.5">{ja ? 'Start Free — 60秒で登録' : 'It takes 60 seconds'}</p>
                  </div>

                  {/* Scarcity reinforcement next to the form (counter also in hero) */}
                  <div className="mb-4 flex items-center justify-center gap-2 rounded-xl bg-emerald-50 ring-1 ring-emerald-200 px-3 py-2" data-testid="ad-form-scarcity">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                    </span>
                    <span className="text-[12px] font-bold text-emerald-800 tabular-nums" data-testid="ad-form-remaining">
                      {ja ? `先行受付枠 残り ${fmt(remaining)} ／ 100,000` : `${fmt(remaining)} of 100,000 spots left`}
                    </span>
                  </div>

                  {/* Honeypot (hidden from humans) */}
                  <input
                    type="text" tabIndex={-1} autoComplete="off"
                    className="hidden" aria-hidden="true"
                    onChange={(e) => { honeypot.current = e.target.value; }}
                  />

                  <FieldLabel jt="メールアドレス" en="Email Address" />
                  <div className="relative mb-4">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="email" required value={form.email}
                      onChange={(e) => { markFormStarted(); setForm({ ...form, email: e.target.value }); }}
                      placeholder="example@company.co.jp"
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-[#9200C7]/30 focus:border-[#9200C7]"
                      data-testid="ad-email-input"
                    />
                  </div>

                  <FieldLabel jt="アカウント種別" en="Account Type" />
                  {isTargeted && !showTypeChooser ? (
                    <div className="flex items-center justify-between gap-2 mb-4 rounded-xl border border-[#9200C7]/30 bg-[#9200C7]/5 px-3 py-2.5" data-testid="ad-account-type-confirmed">
                      <span className="inline-flex items-center gap-2 text-sm font-bold text-[#9200C7]">
                        <Check className="w-4 h-4" strokeWidth={3} />
                        {ja
                          ? (ACCOUNT_TYPES.find((t) => t.key === track) || {}).ja
                          : (ACCOUNT_TYPES.find((t) => t.key === track) || {}).en}
                      </span>
                      <button
                        type="button" onClick={() => setShowTypeChooser(true)}
                        className="text-[11px] font-bold text-slate-500 underline hover:text-[#9200C7]"
                        data-testid="ad-account-type-change"
                      >
                        {ja ? '変更' : 'Change'}
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-1.5 mb-4" data-testid="ad-account-type">
                      {ACCOUNT_TYPES.map((t) => (
                        <button
                          key={t.key} type="button"
                          onClick={() => setTrack(t.key)}
                          className={`px-2 py-2 rounded-xl text-xs font-bold transition-all border ${track === t.key ? 'bg-[#9200C7] text-white border-[#9200C7] shadow' : 'bg-white text-slate-600 border-slate-200 hover:border-[#9200C7]/40'}`}
                          data-testid={`ad-account-type-${t.key}`}
                        >
                          {ja ? t.ja : t.en}
                        </button>
                      ))}
                    </div>
                  )}

                  {isOrg && (
                    <>
                      <FieldLabel jt="会社名・組織名（任意）" en="Organization (optional)" />
                      <input
                        type="text" value={form.organization_name}
                        onChange={(e) => setForm({ ...form, organization_name: e.target.value })}
                        placeholder={ja ? '例）株式会社サンプル' : 'e.g. Sample Co., Ltd.'}
                        className="w-full px-3 py-2.5 mb-4 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-[#9200C7]/30 focus:border-[#9200C7]"
                        data-testid="ad-org-input"
                      />
                    </>
                  )}

                  {error && (
                    <p className="mb-3 text-xs font-bold text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="ad-form-error">{error}</p>
                  )}

                  <button
                    type="submit" disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-black text-white bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-500 hover:to-green-400 shadow-lg shadow-emerald-500/20 transition-all disabled:opacity-60"
                    data-testid="ad-submit-button"
                  >
                    {loading
                      ? (ja ? '送信中…' : 'Submitting…')
                      : (<>{ja ? '先行受付に登録（1年間無料）' : 'Join Early Access — 1 year free'}<ArrowRight className="w-4 h-4" strokeWidth={2.6} /></>)}
                  </button>

                  <p className="mt-3 text-center text-[11px] text-slate-400 flex items-center justify-center gap-1">
                    <Lock className="w-3 h-3" />
                    {ja ? 'お客様の情報は安全に保護されます。' : 'Your information is secure and never shared.'}
                  </p>

                  {/* Non-blocking legal disclosure (no checkbox — low-friction signup) */}
                  <p className="mt-2 text-center text-[10px] text-slate-400 leading-snug">
                    {ja
                      ? <>登録すると<a {...ext('/terms')} className="text-[#9200C7] underline">利用規約</a>・<a {...ext('/privacy')} className="text-[#9200C7] underline">プライバシーポリシー</a>に同意したものとみなされます。</>
                      : <>By joining you agree to our <a {...ext('/terms')} className="text-[#9200C7] underline">Terms</a> & <a {...ext('/privacy')} className="text-[#9200C7] underline">Privacy Policy</a>.</>}
                  </p>

                  {/* Encourage research on the main site (opens new tab) */}
                  <a
                    {...ext('')}
                    className="mt-3 flex items-center justify-center gap-1.5 text-xs font-bold text-slate-500 hover:text-[#9200C7] transition-colors"
                    data-testid="ad-form-learn-more"
                  >
                    <Globe className="w-3.5 h-3.5" />
                    {ja ? 'まずはサービス内容を詳しく見る' : 'New here? Learn more on the main site'}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </form>
              )}
            </div>

            {/* Value points (DOM 3 → mobile below form; desktop left under headline) */}
            <div className="mt-10 lg:mt-10 lg:col-span-7 lg:col-start-1 lg:row-start-2" data-testid="ad-value-points">
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3.5">
                {VALUE_POINTS.map(({ Icon, ja: jt, en }, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-500/15 ring-1 ring-emerald-400/25 text-emerald-300">
                      <Icon className="w-4 h-4" strokeWidth={2.2} />
                    </span>
                    <span className="text-sm text-white/90 leading-snug pt-1.5">{ja ? jt : en}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── COMPACT TRUST STRIP (placement: directly under hero) ──────────
          Reuses already-approved phrasing (PDF・CSV / encryption / daily
          auto-delivery). No new claims, no affiliation implication. Gives
          early trust signals above the fold on the scroll path. */}
      <section className="bg-white border-t border-slate-100" data-testid="ad-trust-strip">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 grid grid-cols-3 gap-2 sm:gap-6 divide-x divide-slate-100">
          {[
            { Icon: FileText, ja: 'PDF・CSV 対応', en: 'PDF & CSV' },
            { Icon: ShieldCheck, ja: '安全に暗号化', en: 'Encrypted & secure' },
            { Icon: Clock, ja: '毎日自動配信', en: 'Automated daily' },
          ].map(({ Icon, ja: jt, en }, i) => (
            <div key={i} className="flex items-center justify-center gap-2 px-1 text-center" data-testid={`ad-trust-strip-item-${i}`}>
              <Icon className="w-4 h-4 text-emerald-600 shrink-0" strokeWidth={2.2} />
              <span className="text-[11px] sm:text-[13px] font-bold text-slate-700 leading-tight">{ja ? jt : en}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── BRAND SHOWCASE BANNER (language-aware) ────────────────────── */}
      <section className="px-4 sm:px-6 lg:px-8 py-10 bg-white border-t border-slate-100" data-testid="ad-brand-banner">
        <div className="max-w-6xl mx-auto rounded-3xl overflow-hidden ring-1 ring-black/5 shadow-xl">
          <picture>
            <source srcSet={ja ? HERO_BANNER_JA_WEBP : HERO_BANNER_EN_WEBP} type="image/webp" />
            <img
              src={ja ? HERO_BANNER_JA : HERO_BANNER_EN}
              alt={ja ? 'ETC利用履歴をもっと簡単に。毎日メールでお届け。' : 'Smarter, safer ETC usage history, delivered daily.'}
              width={2033}
              height={774}
              loading="lazy"
              decoding="async"
              className="w-full h-auto object-contain"
              data-testid="ad-brand-banner-image"
            />
          </picture>
        </div>
      </section>

      {/* ── REAL-TIME FLOW ANIMATION ──────────────────────────────────── */}
      <section className="px-4 sm:px-6 lg:px-8 py-14 bg-slate-50 border-t border-slate-100" data-testid="ad-flow-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-7">
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#9200C7] mb-2">
              {ja ? 'リアルタイムの流れ' : 'See it in real time'}
            </p>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
              {ja ? '毎日のレシートが届くまで' : 'How your daily receipts get delivered'}
            </h2>
            <p className="mt-2 text-sm text-slate-500 max-w-2xl mx-auto leading-relaxed">
              {ja
                ? <Jp>JTRが利用明細を取得し、検証して、PDF・CSVのレポートとしてあなたに配信するまでの流れを、リアルタイムでご覧いただけます。</Jp>
                : 'Watch how JTR retrieves your toll usage, validates it, and distributes your receipts as PDF + CSV — automatically, in real time.'}
            </p>
          </div>
          <SimulatedAIPipelineDemo variant={isOrg ? 'business' : 'personal'} />
        </div>
      </section>

      {/* ── FEATURES GRID ─────────────────────────────────────────────── */}
      <section className="px-4 sm:px-6 lg:px-8 py-14 bg-white border-t border-slate-100" data-testid="ad-features">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-9">
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900">
              {ja ? '業務を効率化し、コストを最適化する機能' : 'Powerful features that drive efficiency'}
            </h2>
            <p className="mt-1 text-sm text-slate-400">
              {ja ? 'Powerful features that drive efficiency and reduce costs' : '業務を効率化し、コストを最適化'}
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map(({ Icon, ja: jt, en, dja, den }, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#9200C7]/30 hover:shadow-md transition-all" data-testid={`ad-feature-${i}`}>
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-purple-50 text-[#9200C7] mb-3">
                  <Icon className="w-5 h-5" strokeWidth={2.2} />
                </span>
                <h3 className="text-sm font-black text-slate-900 leading-tight">{ja ? jt : en}</h3>
                <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">{ja ? dja : den}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURE STORY BAND 1 (automation) — LEAD band (larger H2 for scan-path) ─ */}
      <FeatureImageBand
        testid="ad-band-automation"
        jp={ja}
        lead
        img={TEAM_PHOTOS[0]}
        bg="bg-white"
        kicker={ja ? '自動化' : 'Automation'}
        h={ja ? '毎日のETC利用明細を、自動で取得' : 'Your ETC toll history, retrieved automatically'}
        p={ja
          ? 'JTRがETC利用照会サービスから明細を取得し、検証して、毎日のレポートとしてお届けします。面倒な手作業はもう必要ありません。'
          : 'JTR retrieves your toll usage from the ETC inquiry service, validates it, and delivers a daily report — no more manual work.'}
      />

      {/* ── SEO CONTENT: WHO IT'S FOR ─────────────────────────────────── */}
      <section className="px-4 sm:px-6 lg:px-8 py-12 bg-slate-50 border-t border-slate-100" data-testid="ad-who-for">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900">
            {ja ? 'Japan Toll Receipts は、こんな方に' : 'Who is Japan Toll Receipts for?'}
          </h2>
          <p className="mt-1 text-sm text-slate-400">{ja ? 'Who is Japan Toll Receipts for?' : 'Built for every kind of ETC user in Japan'}</p>
          <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {WHO_FOR.map(({ Icon, ja: jt, en }, i) => (
              <div key={i} className="flex items-center gap-2.5 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left" data-testid={`ad-whofor-${i}`}>
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-purple-50 text-[#9200C7] shrink-0"><Icon className="w-4 h-4" strokeWidth={2.2} /></span>
                <span className="text-[13px] font-bold text-slate-700 leading-tight">{ja ? jt : en}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURE STORY BAND 2 (reports) ────────────────────────────── */}
      <FeatureImageBand
        testid="ad-band-reports"
        jp={ja}
        img={TEAM_PHOTOS[1]}
        imgRight
        bg="bg-slate-50"
        kicker={ja ? 'レポート' : 'Reports'}
        h={ja ? 'PDF・CSVで、すぐ使えるレポート' : 'Ready-to-use PDF & CSV reports'}
        p={ja
          ? '経費精算・記録整理・社内管理に。必要な形式でレポートをダウンロード、またはメールで受け取れます。'
          : 'For reimbursement, record keeping, and internal records — download or receive your reports in the exact format you need.'}
      />

      {/* ── SEO CONTENT: PROBLEMS WE SOLVE ────────────────────────────── */}
      <section className="px-4 sm:px-6 lg:px-8 py-12 bg-white border-t border-slate-100" data-testid="ad-problems">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900">{ja ? 'こんな課題を解決します' : 'Common problems we solve'}</h2>
            <p className="mt-1 text-sm text-slate-400">{ja ? 'Common problems we solve' : 'The everyday ETC headaches JTR removes'}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {cfg.problems.map((p, i) => (
              <div key={i} className="flex items-start gap-2.5 rounded-2xl border border-slate-200 bg-slate-50/60 px-4 py-3" data-testid={`ad-problem-${i}`}>
                <AlertCircle className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" strokeWidth={2.4} />
                <span className="text-[13px] font-semibold text-slate-700 leading-snug">{ja ? p.ja : p.en}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURE STORY BAND 3 (centralize) ─────────────────────────── */}
      <FeatureImageBand
        testid="ad-band-manage"
        jp={ja}
        img={TEAM_PHOTOS[2]}
        bg="bg-white"
        kicker={ja ? '一元管理' : 'One place'}
        h={ja ? '車両とETCカードを、ひとつの画面で' : 'Vehicles & ETC cards, all in one place'}
        p={ja
          ? '複数のカード・車両・拠点をまとめて管理。コストを可視化し、組織全体の利用状況をひと目で把握できます。'
          : 'Manage multiple cards, vehicles, and branches together — with clear cost visibility across your whole organization.'}
      />

      {/* ── SEO CONTENT: HOW IT WORKS ─────────────────────────────────── */}
      <section className="px-4 sm:px-6 lg:px-8 py-12 bg-slate-50 border-t border-slate-100" data-testid="ad-how-it-works">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-9">
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#9200C7] mb-2 inline-flex items-center gap-1.5 justify-center"><Workflow className="w-3.5 h-3.5" /> {ja ? '仕組み' : 'How it works'}</p>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900">{ja ? '4ステップで、毎日のレシートを自動化' : 'A simple 4-step process'}</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {HOW_STEPS.map(({ Icon, ja: jt, en, dja, den }, i) => (
              <div key={i} className="relative rounded-2xl border border-slate-200 bg-white p-5" data-testid={`ad-how-step-${i}`}>
                <span className="absolute top-3 right-3 text-3xl font-black text-slate-100">{i + 1}</span>
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-purple-50 text-[#9200C7] mb-3"><Icon className="w-5 h-5" strokeWidth={2.2} /></span>
                <h3 className="text-sm font-black text-slate-900 leading-tight">{ja ? jt : en}</h3>
                <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">{ja ? dja : den}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURE STORY BAND 4 (security) ───────────────────────────── */}
      <FeatureImageBand
        testid="ad-band-security"
        jp={ja}
        img={TEAM_PHOTOS[3]}
        imgRight
        bg="bg-slate-50"
        kicker={ja ? 'セキュリティ' : 'Security'}
        h={ja ? '安心・安全のクラウド管理' : 'Secure, reliable cloud management'}
        p={ja
          ? 'データは暗号化して安全に保管。企業レベルのセキュリティで、大切な情報をしっかり守ります。'
          : 'Your data is encrypted and stored securely, protected with enterprise-grade security you can rely on.'}
      />

      {/* ── SEO CONTENT: FAQ ──────────────────────────────────────────── */}
      <section className="px-4 sm:px-6 lg:px-8 py-12 bg-white border-t border-slate-100" data-testid="ad-faq">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-7">
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#9200C7] mb-2 inline-flex items-center gap-1.5 justify-center"><HelpCircle className="w-3.5 h-3.5" /> {ja ? 'よくあるご質問' : 'Frequently asked'}</p>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900">{ja ? 'よくあるご質問' : 'Frequently Asked Questions'}</h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {faq.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`ad-faq-${i}`}>
                <AccordionTrigger className="text-left text-[14px] font-bold text-slate-800">{ja ? f.qja : f.qen}</AccordionTrigger>
                <AccordionContent className="text-[13px] text-slate-600 leading-relaxed">{ja ? f.aja : f.aen}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ── FEATURE STORY BAND 5 (team) ───────────────────────────────── */}
      <FeatureImageBand
        testid="ad-band-team"
        jp={ja}
        img={TEAM_PHOTOS[4]}
        bg="bg-white"
        kicker={ja ? '専任チーム' : 'Our team'}
        h={ja ? 'AIと専任チームが、毎日サポート' : 'AI and a dedicated team, supporting you daily'}
        p={ja
          ? 'AIによる自動化と専任スタッフの確認で、正確で信頼できるレポートを毎日お届けします。'
          : 'AI automation plus dedicated specialists deliver accurate, dependable reports every single day.'}
      />

      {/* ── TRUST BAND ────────────────────────────────────────────────── */}
      <section className="px-4 sm:px-6 lg:px-8 py-12 bg-gradient-to-br from-[#0a1a33] via-[#15103b] to-[#241046] text-white" data-testid="ad-trust">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-center text-xl sm:text-2xl font-black tracking-tight mb-1">
            {ja ? '安心してご利用いただくために' : 'Built for trust & reliability'}
          </h2>
          <p className="text-center text-xs text-white/75 mb-8">
            {ja ? 'Independent service · 独立した第三者サービス' : 'An independent third-party service'}
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {TRUST.map(({ Icon, ja: jt, en, dja, den }, i) => (
              <div key={i} className="flex flex-col items-center text-center" data-testid={`ad-trust-${i}`}>
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white/10 ring-1 ring-white/15 text-emerald-300 mb-3">
                  <Icon className="w-5 h-5" strokeWidth={2} />
                </span>
                <h3 className="text-sm font-black">{ja ? jt : en}</h3>
                <p className="text-[11px] text-white/60 mt-1 leading-snug">{ja ? dja : den}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-[11px] text-white/70 max-w-3xl mx-auto leading-relaxed">
            {ja
              ? 'Japan Toll Receipts は独立したレポート・自動化サービスです。NEXCO・ETC利用照会サービス・有料道路事業者ではありません。'
              : 'Japan Toll Receipts is an independent reporting & automation service. It is not NEXCO, the ETC Usage Inquiry Service, or a toll road operator.'}
          </p>
        </div>
      </section>

      {/* ── LEARN MORE (external links, new tab) ──────────────────────── */}
      <section className="px-4 sm:px-6 lg:px-8 py-10 bg-white border-t border-slate-100" data-testid="ad-learn-more">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
          <div>
            <h3 className="text-lg font-black text-slate-900">{ja ? '詳しく知りたい方はこちら' : 'Want to learn more?'}</h3>
            <p className="text-xs text-slate-400">{ja ? '新しいタブで開きます' : 'Opens in a new tab'}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a {...ext('/features')} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-bold text-slate-700 hover:border-[#9200C7]/40 hover:text-[#9200C7] transition-colors" data-testid="ad-link-features">
              {ja ? 'サービス詳細を見る' : 'View Service Details'}
              <ExternalLink className="w-4 h-4" />
            </a>
            <a {...ext('/business/organization-management')} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-bold text-slate-700 hover:border-[#9200C7]/40 hover:text-[#9200C7] transition-colors" data-testid="ad-link-org">
              {ja ? '法人・官公庁向け管理' : 'Business / Government'}
              <ExternalLink className="w-4 h-4" />
            </a>
            <a {...ext('')} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-colors" data-testid="ad-link-main">
              <Globe className="w-4 h-4" />
              {ja ? 'メインサイトを開く' : 'Open Main Site'}
            </a>
          </div>
        </div>
      </section>

      {/* ── FOOTER ────────────────────────────────────────────────────── */}
      <footer className="px-4 sm:px-6 lg:px-8 py-6 bg-slate-50 border-t border-slate-100" data-testid="ad-footer">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[11px] text-slate-400">
          <div className="flex flex-col gap-1" data-testid="ad-footer-nojo">
            <p className="text-[11px] text-slate-500">
              © 2013–{new Date().getFullYear()} NoJo Enterprise, Co. Ltd. {ja ? 'All Rights Reserved.' : 'All rights reserved.'}
            </p>
            <a
              href="https://www.nojoenterprise.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-slate-700 transition-colors"
              data-testid="ad-footer-nojo-link"
              aria-label={ja ? 'ノージョエンタープライズ株式会社のサイトを開く' : 'Open NoJo Enterprise corporate website (new tab)'}
            >
              <img
                src="/brand/nojoenterprise-logo.png"
                alt=""
                aria-hidden="true"
                className="w-3.5 h-3.5 object-contain"
                width={14}
                height={14}
              />
              <span className="font-medium">www.NoJoEnterprise.com</span>
              <span className="text-slate-400">↗</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <a {...ext('/terms')} className="hover:text-slate-600">{ja ? '利用規約' : 'Terms of Service'}</a>
            <a {...ext('/privacy')} className="hover:text-slate-600">{ja ? 'プライバシーポリシー' : 'Privacy Policy'}</a>
          </div>
        </div>
      </footer>

      {/* Sticky mobile CTA — long page means the hero form scrolls away on
          phones; this keeps a one-tap path back to signup. Hidden once joined. */}
      {!joined && !success && (
        <div
          className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-2.5 flex items-center gap-3"
          data-testid="ad-sticky-cta"
        >
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold text-emerald-700 tabular-nums truncate">
              {ja ? `残り ${fmt(remaining)} ／ 100,000` : `${fmt(remaining)} of 100,000 left`}
            </p>
            <p className="text-[10px] text-slate-400 truncate">{ja ? '1年間無料・60秒で登録' : '1 year free · 60 seconds'}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById('ad-signup');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              setTimeout(() => { const i = document.querySelector("[data-testid='ad-email-input']"); if (i) i.focus(); }, 500);
            }}
            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-black text-white bg-gradient-to-r from-emerald-600 to-green-500 shadow-lg shadow-emerald-500/20"
            data-testid="ad-sticky-cta-button"
          >
            {ja ? '先行受付に登録' : 'Join Early Access'}<ArrowRight className="w-4 h-4" strokeWidth={2.6} />
          </button>
        </div>
      )}
    </div>
  );
}

function FeatureImageBand({ testid, img, imgRight = false, bg = 'bg-white', kicker, h, p, jp = false, lead = false }) {
  return (
    <section className={`px-4 sm:px-6 lg:px-8 py-12 lg:py-16 ${bg} border-t border-slate-100`} data-testid={testid}>
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
        <div className={`rounded-3xl overflow-hidden ring-1 ring-black/5 shadow-xl ${imgRight ? 'lg:order-2' : 'lg:order-1'}`}>
          <img src={img} alt={typeof h === 'string' ? h : ''} className="w-full h-auto object-cover" loading="lazy" />
        </div>
        <div className={imgRight ? 'lg:order-1' : 'lg:order-2'}>
          <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#9200C7] mb-2.5">{kicker}</p>
          <h2 className={`${lead ? 'text-3xl sm:text-4xl lg:text-5xl' : 'text-2xl sm:text-3xl'} font-black tracking-tight text-slate-900 leading-tight`}>
            {jp ? <Jp>{h}</Jp> : h}
          </h2>
          <p className={`mt-4 ${lead ? 'text-lg' : 'text-base'} text-slate-600 leading-relaxed`}>
            {jp ? <Jp>{p}</Jp> : p}
          </p>
        </div>
      </div>
    </section>
  );
}

function JoinedCard({ ja, lang }) {
  const [copied, setCopied] = useState(false);
  // Share the clean signup URL (no joined param) so recipients see the form.
  const shareUrl = `${window.location.origin}${window.location.pathname}`;
  const onShare = async () => {
    const data = {
      title: 'Japan Toll Receipts',
      text: ja ? 'ETC利用明細を自動でレポート化。先行受付受付中。' : 'Automated ETC toll receipts. Early Access is open.',
      url: shareUrl,
    };
    try {
      if (navigator.share) { await navigator.share(data); return; }
    } catch { return; /* user cancelled native share */ }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — best effort */ }
  };
  const PLANNED = [
    { ja: 'ETC利用履歴レポートの自動配信予定', en: 'Automated ETC usage history reports (planned)' },
    { ja: 'PDFレポート生成', en: 'PDF report generation' },
    { ja: 'CSVデータ出力', en: 'CSV data export' },
    { ja: '車両・ETCカード管理', en: 'Vehicle & ETC card management' },
    { ja: '個人・法人・団体向けに対応予定', en: 'For individuals, businesses & organizations (planned)' },
  ];
  return (
    <div className="rounded-3xl bg-white shadow-2xl ring-1 ring-black/5 p-5 sm:p-6" data-testid="ad-joined-card">
      <div className="text-center">
        <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 mb-4">
          <Check className="w-7 h-7" strokeWidth={3} />
        </span>
        <h2 className="text-xl font-black text-slate-900">
          {ja ? '先行受付登録ありがとうございます' : 'Thank you for joining Early Access'}
        </h2>
        <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">
          {ja
            ? 'Japan Toll Receipts の先行受付にご登録いただきありがとうございます。正式サービス開始に向けて、今後のご案内や更新情報をメールでお送りします。このページでは、Japan Toll Receipts でできることをご確認いただけます。'
            : 'Thank you for joining Japan Toll Receipts Early Access. We will email you announcements and updates as we approach our official launch. On this page, you can explore what Japan Toll Receipts will do for you.'}
        </p>
        <div className="mt-4 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-4 py-3 text-left" data-testid="ad-joined-confirm-email">
          <p className="text-[13px] font-bold text-amber-900 flex items-start gap-2 leading-snug">
            <Mail className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.4} />
            {ja
              ? '確認メールをお送りしました。メール内のリンクをクリックして、先行受付登録を完了してください。（メールが届かない場合は、迷惑メールフォルダもご確認ください。）'
              : 'We\u2019ve sent you a confirmation email. Please click the link inside to finish your Early Access registration. (If you don\u2019t see it, check your spam folder.)'}
          </p>
        </div>
      </div>

      <div className="mt-5 rounded-2xl bg-slate-50 ring-1 ring-slate-100 p-4">
        <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#9200C7] mb-3">
          {ja ? 'Japan Toll Receipts でできること' : 'What Japan Toll Receipts will do'}
        </p>
        <ul className="space-y-2.5">
          {PLANNED.map(({ ja: jt, en }, i) => (
            <li key={i} className="flex items-start gap-2.5" data-testid={`ad-joined-next-${i}`}>
              <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" strokeWidth={3} />
              <span className="text-[13px] font-semibold text-slate-700 leading-snug">{ja ? jt : en}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-5 grid gap-2.5">
        <button
          type="button"
          onClick={onShare}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-black text-white bg-gradient-to-r from-[#9200C7] to-[#6b21a8] hover:opacity-95 shadow-lg shadow-purple-500/20 transition-all"
          data-testid="ad-joined-share"
        >
          <Share2 className="w-4 h-4" strokeWidth={2.4} />
          {copied
            ? (ja ? 'リンクをコピーしました' : 'Link copied!')
            : (ja ? '会社・チームに共有する' : 'Share with your company / team')}
        </button>
        <a
          href={`/${lang}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-slate-700 border border-slate-200 hover:border-[#9200C7]/40 hover:text-[#9200C7] transition-colors"
          data-testid="ad-joined-website"
        >
          <Globe className="w-4 h-4" />
          {ja ? 'メインサイトを見る' : 'Visit the full website'}
        </a>
      </div>

      <p className="mt-4 text-[11px] text-slate-400 leading-relaxed" data-testid="ad-joined-disclaimer">
        {ja
          ? 'Japan Toll Receipts は、NEXCO、ETC利用照会サービス、または有料道路事業者とは関係がありません。'
          : 'Japan Toll Receipts is not an official service operated by NEXCO, the ETC Usage Inquiry Service, or any toll road operator.'}
      </p>
    </div>
  );
}

function SuccessCard({ ja, data, lang }) {
  const trackingId = data?.tracking_id || '';
  return (
    <div className="rounded-3xl bg-white shadow-2xl ring-1 ring-black/5 p-6 text-center" data-testid="ad-success-card">
      <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 mb-4">
        <Check className="w-7 h-7" strokeWidth={3} />
      </span>
      <h2 className="text-xl font-black text-slate-900">{ja ? 'ご登録ありがとうございます！' : "You're on the list!"}</h2>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">
        {ja
          ? '確認メールをお送りしました。メール内のリンクをクリックして登録を完了してください。'
          : 'We sent you a confirmation email. Please click the link inside to complete your Early Access request.'}
      </p>
      {trackingId && (
        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-purple-50 ring-1 ring-purple-100 px-4 py-1.5" data-testid="ad-success-tracking">
          <Sparkles className="w-3.5 h-3.5 text-[#9200C7]" />
          <span className="text-xs font-black text-[#9200C7]">{ja ? '受付番号' : 'Tracking ID'}: {trackingId}</span>
        </div>
      )}
      <a
        href={`/${lang}`} target="_blank" rel="noopener noreferrer"
        className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-bold text-slate-700 hover:border-[#9200C7]/40 hover:text-[#9200C7] transition-colors"
        data-testid="ad-success-main-link"
      >
        <Globe className="w-4 h-4" />
        {ja ? 'メインサイトを見る' : 'Explore the main site'}
      </a>
    </div>
  );
}
